import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HeartPulse, ShieldCheck, LogOut, Users, Stethoscope, ClipboardList, CheckCircle2, XCircle, Building2, Plus, Pencil, Trash2, Upload, Pause, Play, Ban } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api, apiError, imgUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";

const ACCESS_META = {
  active: { label: "Activo", cls: "border-emerald-200 bg-emerald-50 text-emerald-600" },
  paused: { label: "Pausado", cls: "border-amber-200 bg-amber-50 text-amber-600" },
  revoked: { label: "Revocado", cls: "border-red-200 bg-red-50 text-red-500" },
};

export default function AdminPanel() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [users, setUsers] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [hForm, setHForm] = useState({ name: "", city: "", address: "" });
  const [editingId, setEditingId] = useState(null);
  const logoRef = useRef(null);
  const logoTargetId = useRef(null);

  const load = async () => {
    const [s, d, u, h] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/doctors"),
      api.get("/admin/users"),
      api.get("/hospitals"),
    ]);
    setStats(s.data);
    setDoctors(d.data);
    setUsers(u.data);
    setHospitals(h.data);
  };
  useEffect(() => { load(); }, []);

  const setLicense = async (id, status) => {
    try {
      await api.patch(`/admin/doctors/${id}/license`, { status });
      toast.success(status === "approved" ? "Licencia aprobada" : "Licencia actualizada");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const setAccess = async (id, status) => {
    try {
      await api.patch(`/admin/doctors/${id}/access`, { status });
      toast.success(status === "active" ? "Acceso reactivado" : status === "paused" ? "Médico pausado" : "Acceso revocado");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const toggleUser = async (id, active) => {
    await api.patch(`/admin/users/${id}`, { active: !active });
    load();
  };

  const submitHospital = async (e) => {
    e.preventDefault();
    if (!hForm.name.trim() || !hForm.city.trim()) return;
    try {
      if (editingId) {
        await api.put(`/admin/hospitals/${editingId}`, hForm);
        toast.success("Hospital actualizado");
      } else {
        await api.post("/admin/hospitals", hForm);
        toast.success("Hospital agregado");
      }
      setHForm({ name: "", city: "", address: "" });
      setEditingId(null);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const editHospital = (h) => { setEditingId(h.id); setHForm({ name: h.name, city: h.city, address: h.address || "" }); };

  const deleteHospital = async (id) => {
    await api.delete(`/admin/hospitals/${id}`);
    toast.success("Hospital eliminado");
    if (editingId === id) { setEditingId(null); setHForm({ name: "", city: "", address: "" }); }
    load();
  };

  const pickLogo = (id) => { logoTargetId.current = id; logoRef.current?.click(); };
  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !logoTargetId.current) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/hospitals/${logoTargetId.current}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo actualizado");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { if (logoRef.current) logoRef.current.value = ""; logoTargetId.current = null; }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-20 border-b border-[#E2E8F0] bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-3">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0284C7] text-white"><HeartPulse className="h-4 w-4" strokeWidth={2.6} /></div>
            <span className="text-base font-extrabold tracking-tight text-slate-900">SaludCitaRD · Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm font-bold text-slate-700">{user?.name}</span>
            <Button variant="ghost" onClick={async () => { await logout(); navigate("/login"); }} className="rounded-full text-slate-400" data-testid="logout-btn"><LogOut className="h-4 w-4" /></Button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-6 py-8">
        <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Panel de administración</h1>

        <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard icon={Users} label="Pacientes" value={stats?.patients} />
          <StatCard icon={Stethoscope} label="Médicos" value={stats?.doctors} />
          <StatCard icon={ShieldCheck} label="Licencias pendientes" value={stats?.pending_licenses} />
          <StatCard icon={ClipboardList} label="Citas" value={stats?.appointments} />
        </div>

        <Tabs defaultValue="licenses" className="mt-8">
          <TabsList className="rounded-full bg-white border border-[#E2E8F0] p-1">
            <TabsTrigger value="licenses" className="rounded-full font-bold data-[state=active]:bg-[#0284C7] data-[state=active]:text-white" data-testid="tab-licenses">Validar licencias</TabsTrigger>
            <TabsTrigger value="users" className="rounded-full font-bold data-[state=active]:bg-[#0284C7] data-[state=active]:text-white" data-testid="tab-users">Usuarios</TabsTrigger>
            <TabsTrigger value="hospitals" className="rounded-full font-bold data-[state=active]:bg-[#0284C7] data-[state=active]:text-white" data-testid="tab-hospitals">Hospitales</TabsTrigger>
            <TabsTrigger value="medicos" className="rounded-full font-bold data-[state=active]:bg-[#0284C7] data-[state=active]:text-white" data-testid="tab-medicos">Médicos</TabsTrigger>
          </TabsList>

          <TabsContent value="licenses" className="mt-5">
            <div className="overflow-hidden rounded-2xl border border-[#E2E8F0] bg-white">
              <table className="w-full text-left">
                <thead><tr className="border-b border-[#E2E8F0] text-xs font-bold uppercase tracking-wider text-slate-400">
                  <th className="px-5 py-3">Médico</th><th className="px-5 py-3">Especialidad</th><th className="px-5 py-3">Licencia</th><th className="px-5 py-3">Estado</th><th className="px-5 py-3 text-right">Acciones</th>
                </tr></thead>
                <tbody>
                  {doctors.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-sm font-bold text-slate-400">Sin médicos registrados.</td></tr>}
                  {doctors.map((d) => (
                    <tr key={d.user_id} className="border-b border-[#E2E8F0] last:border-0 hover:bg-slate-50" data-testid={`admin-doctor-${d.user_id}`}>
                      <td className="px-5 py-3 text-sm font-extrabold text-slate-900">{d.name}<div className="text-xs font-bold text-slate-400">{d.email}</div></td>
                      <td className="px-5 py-3 text-sm font-bold text-slate-500">{d.doctor_profile?.specialty || "—"}</td>
                      <td className="px-5 py-3 text-sm font-bold text-slate-500">{d.doctor_profile?.license_number || "—"}</td>
                      <td className="px-5 py-3"><StatusBadge status={d.license_status} /></td>
                      <td className="px-5 py-3">
                        <div className="flex justify-end gap-1.5">
                          <button onClick={() => setLicense(d.user_id, "approved")} className="rounded-full bg-emerald-500 p-2 text-white hover:scale-105 transition-transform" title="Aprobar" data-testid={`approve-${d.user_id}`}><CheckCircle2 className="h-3.5 w-3.5" /></button>
                          <button onClick={() => setLicense(d.user_id, "rejected")} className="rounded-full bg-red-500 p-2 text-white hover:scale-105 transition-transform" title="Rechazar" data-testid={`reject-${d.user_id}`}><XCircle className="h-3.5 w-3.5" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="users" className="mt-5">
            <div className="overflow-hidden rounded-2xl border border-[#E2E8F0] bg-white">
              <table className="w-full text-left">
                <thead><tr className="border-b border-[#E2E8F0] text-xs font-bold uppercase tracking-wider text-slate-400">
                  <th className="px-5 py-3">Nombre</th><th className="px-5 py-3">Correo</th><th className="px-5 py-3">Rol</th><th className="px-5 py-3">Estado</th><th className="px-5 py-3 text-right">Acción</th>
                </tr></thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.user_id} className="border-b border-[#E2E8F0] last:border-0 hover:bg-slate-50" data-testid={`admin-user-${u.user_id}`}>
                      <td className="px-5 py-3 text-sm font-extrabold text-slate-900">{u.name}</td>
                      <td className="px-5 py-3 text-sm font-bold text-slate-500">{u.email}</td>
                      <td className="px-5 py-3"><span className="rounded-full border border-[#E2E8F0] px-3 py-1 text-xs font-bold text-slate-500">{u.role}</span></td>
                      <td className="px-5 py-3"><span className={`rounded-full border px-3 py-1 text-xs font-bold ${u.active ? "border-emerald-200 bg-emerald-50 text-emerald-600" : "border-slate-200 bg-slate-100 text-slate-400"}`}>{u.active ? "Activo" : "Inactivo"}</span></td>
                      <td className="px-5 py-3 text-right">
                        {u.role !== "admin" && (
                          <Button size="sm" variant="outline" onClick={() => toggleUser(u.user_id, u.active)} className="rounded-full border-[#E2E8F0] font-bold" data-testid={`toggle-user-${u.user_id}`}>
                            {u.active ? "Desactivar" : "Activar"}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="hospitals" className="mt-5">
            <input ref={logoRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" hidden onChange={uploadLogo} data-testid="hospital-logo-input" />
            <form onSubmit={submitHospital} className="mb-4 grid gap-3 rounded-2xl border border-[#E2E8F0] bg-white p-5 sm:grid-cols-2">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-400">Nombre del hospital</label>
                <Input value={hForm.name} onChange={(e) => setHForm((f) => ({ ...f, name: e.target.value }))} className="mt-1.5 rounded-xl border-[#E2E8F0]" data-testid="hospital-name-input" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-400">Ciudad</label>
                <Input value={hForm.city} onChange={(e) => setHForm((f) => ({ ...f, city: e.target.value }))} className="mt-1.5 rounded-xl border-[#E2E8F0]" data-testid="hospital-city-input" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-400">Dirección</label>
                <Input value={hForm.address} onChange={(e) => setHForm((f) => ({ ...f, address: e.target.value }))} className="mt-1.5 rounded-xl border-[#E2E8F0]" data-testid="hospital-address-input" />
              </div>
              <div className="flex gap-2 sm:col-span-2">
                <Button type="submit" className="rounded-full bg-[#0284C7] font-bold hover:bg-[#0369a1]" data-testid="save-hospital-btn">
                  {editingId ? <><Pencil className="mr-1.5 h-4 w-4" /> Actualizar</> : <><Plus className="mr-1.5 h-4 w-4" /> Agregar</>}
                </Button>
                {editingId && (
                  <Button type="button" variant="outline" onClick={() => { setEditingId(null); setHForm({ name: "", city: "", address: "" }); }} className="rounded-full border-[#E2E8F0] font-bold">Cancelar</Button>
                )}
              </div>
            </form>
            <div className="overflow-hidden rounded-2xl border border-[#E2E8F0] bg-white">
              <table className="w-full text-left">
                <thead><tr className="border-b border-[#E2E8F0] text-xs font-bold uppercase tracking-wider text-slate-400">
                  <th className="px-5 py-3">Logo</th><th className="px-5 py-3">Hospital</th><th className="px-5 py-3">Dirección</th><th className="px-5 py-3 text-right">Acciones</th>
                </tr></thead>
                <tbody>
                  {hospitals.length === 0 && <tr><td colSpan={4} className="px-5 py-10 text-center text-sm font-bold text-slate-400">Sin hospitales.</td></tr>}
                  {hospitals.map((h) => (
                    <tr key={h.id} className="border-b border-[#E2E8F0] last:border-0 hover:bg-slate-50" data-testid={`admin-hospital-${h.id}`}>
                      <td className="px-5 py-3">
                        <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-lg border border-[#E2E8F0] bg-slate-50">
                          {h.logo_url ? <img src={imgUrl(h.logo_url)} alt="logo" className="h-full w-full object-contain" /> : <Building2 className="h-4 w-4 text-slate-300" />}
                        </div>
                      </td>
                      <td className="px-5 py-3 text-sm font-extrabold text-slate-900">{h.name}<div className="text-xs font-bold text-slate-400">{h.city}</div></td>
                      <td className="px-5 py-3 text-sm font-bold text-slate-500">{h.address || "—"}</td>
                      <td className="px-5 py-3">
                        <div className="flex justify-end gap-1.5">
                          <button onClick={() => pickLogo(h.id)} className="rounded-full bg-slate-700 p-2 text-white hover:scale-105 transition-transform" title="Subir logo" data-testid={`logo-hospital-${h.id}`}><Upload className="h-3.5 w-3.5" /></button>
                          <button onClick={() => editHospital(h)} className="rounded-full bg-[#0284C7] p-2 text-white hover:scale-105 transition-transform" title="Editar" data-testid={`edit-hospital-${h.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                          <button onClick={() => deleteHospital(h.id)} className="rounded-full bg-red-500 p-2 text-white hover:scale-105 transition-transform" title="Eliminar" data-testid={`delete-hospital-${h.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="medicos" className="mt-5">
            <div className="overflow-hidden rounded-2xl border border-[#E2E8F0] bg-white">
              <table className="w-full text-left">
                <thead><tr className="border-b border-[#E2E8F0] text-xs font-bold uppercase tracking-wider text-slate-400">
                  <th className="px-5 py-3">Médico</th><th className="px-5 py-3">Especialidad</th><th className="px-5 py-3">Licencia</th><th className="px-5 py-3">Acceso</th><th className="px-5 py-3 text-right">Acciones</th>
                </tr></thead>
                <tbody>
                  {doctors.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-sm font-bold text-slate-400">Sin médicos registrados.</td></tr>}
                  {doctors.map((d) => {
                    const acc = d.access_status || "active";
                    const meta = ACCESS_META[acc];
                    return (
                      <tr key={d.user_id} className="border-b border-[#E2E8F0] last:border-0 hover:bg-slate-50" data-testid={`medico-${d.user_id}`}>
                        <td className="px-5 py-3 text-sm font-extrabold text-slate-900">{d.name}<div className="text-xs font-bold text-slate-400">{d.email}</div></td>
                        <td className="px-5 py-3 text-sm font-bold text-slate-500">{d.doctor_profile?.specialty || "—"}</td>
                        <td className="px-5 py-3"><StatusBadge status={d.license_status} /></td>
                        <td className="px-5 py-3"><span className={`rounded-full border px-3 py-1 text-xs font-bold ${meta.cls}`} data-testid={`access-${d.user_id}`}>{meta.label}</span></td>
                        <td className="px-5 py-3">
                          <div className="flex flex-wrap justify-end gap-1.5">
                            {acc !== "active" && (
                              <Button size="sm" onClick={() => setAccess(d.user_id, "active")} className="rounded-full bg-emerald-500 font-bold hover:bg-emerald-600" data-testid={`reactivate-${d.user_id}`}><Play className="mr-1 h-3.5 w-3.5" /> Reactivar</Button>
                            )}
                            {acc !== "paused" && (
                              <Button size="sm" variant="outline" onClick={() => setAccess(d.user_id, "paused")} className="rounded-full border-[#E2E8F0] font-bold text-amber-600" data-testid={`pause-${d.user_id}`}><Pause className="mr-1 h-3.5 w-3.5" /> Pausar</Button>
                            )}
                            {acc !== "revoked" && (
                              <Button size="sm" variant="outline" onClick={() => setAccess(d.user_id, "revoked")} className="rounded-full border-[#E2E8F0] font-bold text-red-500" data-testid={`revoke-${d.user_id}`}><Ban className="mr-1 h-3.5 w-3.5" /> Revocar</Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5">
      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-sky-50 text-[#0284C7]"><Icon className="h-4 w-4" /></div>
      <div className="mt-3 text-3xl font-extrabold text-slate-900">{value ?? "—"}</div>
      <div className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</div>
    </div>
  );
}
